/**
 * Scope catalog — mirrors the backend's authoritative list
 * (discovr-backend internal/authz/scopes_catalog.go). The BE assigns these to
 * roles and enforces them at the router; the FE reads the caller's set from
 * GET /api/v1/me/scopes and gates UI on it via useCan() / <Can>.
 *
 * Only the scopes the FE actually gates on need a constant here. Adding one is a
 * one-line edit; the string MUST match the BE catalog exactly (the BE is the
 * source of truth — a typo silently hides UI rather than erroring).
 */
export const SCOPES = {
  // Brand
  brandRead: 'brand:read',
  brandUpdate: 'brand:update',
  // Public read-only links to a brand's reports (Brand Brain, comparisons) — super admin + admin manager
  brandShareWrite: 'brand:share:write',

  // Brand member management (brand_owner-only writes)
  brandMemberList: 'brand_member:list',
  brandMemberInvite: 'brand_member:invite',
  brandMemberSuspend: 'brand_member:suspend',
  brandMemberResume: 'brand_member:resume',
  brandMemberRemove: 'brand_member:remove',
  brandInviteResend: 'brand_invite:resend',
  brandInviteRevoke: 'brand_invite:revoke',

  // Campaign
  campaignCreate: 'campaign:create',
  campaignDelete: 'campaign:delete',
  campaignStatusWrite: 'campaign:status:write',
  campaignBriefSend: 'campaign:brief:send',

  // Roster
  rosterApprove: 'roster:approve',
  rosterReject: 'roster:reject',
  rosterApproveOnBehalfOfBrand: 'roster:approve:on_behalf_of_brand',
  rosterRemove: 'roster:remove',
  rosterHold: 'roster:hold',
  rosterReleaseHold: 'roster:release_hold',
  rosterRollbackApproval: 'roster:rollback_approval',
  rosterUpdate: 'roster:update',
  rosterImport: 'roster:import',
  rosterDeliverablesSelect: 'roster:deliverables:select',
  rosterGoLive: 'roster:go_live',

  // Script / content
  scriptApprove: 'script:approve',
  scriptUpload: 'script:upload',
  contentApprove: 'content:approve',
  contentUpload: 'content:upload',

  // Analytics
  analyticsRead: 'analytics:read',
  analyticsSafetyRead: 'analytics:safety:read',
  // Platform-wide one-shot sentiment backfill (Admin tiers only, not Staff)
  analyticsSentimentBackfill: 'analytics:sentiment:backfill',

  // Reporting page Excel export (admin roles only)
  reportExport: 'report:export',

  // Shopify integration (brand + admin)
  shopifyCouponRead: 'shopify:coupon:read',
  shopifyCouponWrite: 'shopify:coupon:write',
  shopifyConversionsRead: 'shopify:conversions:read',

  // Staff Shopify stores health page (admin + admin_member read; admin write)
  shopifyStoresRead: 'shopify:stores:read',
  shopifyStoresWrite: 'shopify:stores:write',

  // Admin team self-management (admin-only writes)
  adminTeamList: 'admin_team:list',
  adminTeamInvite: 'admin_team:invite',
  adminTeamRemove: 'admin_team:remove',
  // Super Admin only — promote a Staff member to the Admin tier. Not held
  // by the Admin tier itself, so gating "Promote to Admin" UI on this
  // scope naturally hides it for everyone except the Super Admin.
  adminTeamPromote: 'admin_team:promote',

  // Outreach mailbox pool + suppressions (admin tier only — excludes Staff)
  outreachManage: 'outreach:manage',

  // Outreach sourcing (roster-scoped: creators list, contacts, imports — admin app only)
  outreachRead: 'outreach:read',
  outreachWrite: 'outreach:write',

  // Creator management (admin-only)
  creatorList: 'creator:list',
  creatorUpdate: 'creator:update',
  creatorStatusWrite: 'creator:status:write',

  // Creator discovery (admin app only)
  discoveryRead: 'discovery:read',
  discoveryWrite: 'discovery:write',

  // Brand owner activation (super-admin only — resend set-password link)
  brandActivationResend: 'brand:activation:resend',
} as const;

/** A scope string. The SCOPES constants are preferred, but any string is allowed
 *  (the `string & {}` keeps editor autocomplete on the constants without closing
 *  the type to arbitrary BE scopes not yet mirrored here). */
export type Scope = (typeof SCOPES)[keyof typeof SCOPES] | (string & {});

/** Response shape of GET /api/v1/me/scopes (BE: meScopesResponse). */
export interface MeScopes {
  user_id: string;
  role: string;
  brand_id?: string;
  scopes: string[];
  computed_at: string;
}
